<template>
  <div class="daily-report">
    <!-- Page header -->
    <div class="block block-rounded">
      <div class="block-header border-bottom">
        <div>
          <h3 class="block-title">
            <i class="si si-notebook me-1" /> Daily Report
            <span
              v-if="managerName"
              class="fw-normal text-muted"
            > · {{ managerName }}</span>
          </h3>
          <p class="text-muted fs-sm mb-0">
            Manager summary for the team. Sections mirror the daily update format.
          </p>
        </div>
        <div class="block-options d-flex align-items-end gap-2">
          <a
            v-if="backUrl"
            :href="backUrl"
            class="btn btn-sm btn-alt-secondary"
          >
            <i class="fa fa-arrow-left me-1" /> {{ backLabel || 'Back' }}
          </a>
          <div>
            <label
              class="form-label mb-1"
              for="daily-report-date"
            >Report date</label>
            <!-- Picking another day reloads the page for that day: its saved report, or a blank roster. -->
            <input
              id="daily-report-date"
              v-model="reportDate"
              type="date"
              class="form-control form-control-sm"
              :max="todayIso"
              :disabled="!createUrl"
              @change="goToDate"
            >
          </div>
        </div>
      </div>
      <div
        class="alert d-flex flex-wrap align-items-center gap-2 rounded-0 border-0 border-bottom mb-0 py-2 px-3"
        :class="readonly ? 'alert-secondary' : 'alert-info'"
        role="status"
      >
        <i
          class="fa"
          :class="readonly ? 'fa-lock' : initialReport ? 'fa-pen' : 'fa-file'"
          aria-hidden="true"
        />
        <span v-if="readonly">Report for {{ fmtLongDate(reportDate) }} is submitted and locked.</span>
        <span v-else-if="initialReport">Updating the report for {{ fmtLongDate(reportDate) }}.</span>
        <span v-else>New report for {{ fmtLongDate(reportDate) }}.</span>
        <span
          v-if="metaLine"
          class="text-muted fs-sm"
        >· {{ metaLine }}</span>
        <button
          v-if="warnings.length"
          type="button"
          class="badge bg-warning text-dark border-0 daily-report-warnings-badge"
          @click="goToWarning(warnings[0])"
        >
          {{ warnings.length }} {{ warnings.length === 1 ? 'warning' : 'warnings' }}
        </button>
      </div>
      <div class="block-content py-3">
        <div class="row g-3 text-center">
          <div class="col-6 col-md-3">
            <div class="fs-sm text-muted">
              Publish target met
            </div>
            <div
              class="fs-3 fw-semibold"
              :class="publishMetCount === publishRows.length ? 'text-success' : 'text-danger'"
            >
              {{ publishMetCount }} / {{ publishRows.length }}
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="fs-sm text-muted">
              Projects below minimum
            </div>
            <div
              class="fs-3 fw-semibold"
              :class="projectsBelowCount ? 'text-danger' : 'text-success'"
            >
              {{ projectsBelowCount }} / {{ filledProjectCount }}
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="fs-sm text-muted">
              Avg active projects
            </div>
            <div
              class="fs-3 fw-semibold"
              :class="activeAverage >= ACTIVE_AVG ? 'text-success' : 'text-warning'"
            >
              {{ activeAverage }} <span class="fs-sm text-muted">/ {{ ACTIVE_AVG }}</span>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="fs-sm text-muted">
              Credits completed
            </div>
            <div class="fs-3 fw-semibold text-primary">
              {{ creditTotals.completed }} <span class="fs-sm text-muted">/ {{ creditTotals.target }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sections. A filed report is opened read-only, so one fieldset disables every input
         and the project add/remove controls inside it at once. -->
    <fieldset
      class="daily-report-sections"
      :disabled="readonly"
    >
      <!-- 1. Yesterday publish per person -->
      <div class="block block-rounded">
        <div class="block-header border-bottom">
          <div>
            <h3 class="block-title">
              1. Yesterday publish per person
            </h3>
            <p class="text-muted fs-sm mb-0">
              {{ yesterdayLabel }} · minimum {{ PUBLISH_MIN }} per day
            </p>
          </div>
          <div class="block-options d-flex align-items-center gap-2">
            <span
              class="badge"
              :class="publishMetCount === publishRows.length ? 'bg-success' : 'bg-danger'"
            >{{ publishMetCount }} of {{ publishRows.length }} met target</span>
            <button
              v-if="showCharts"
              type="button"
              class="btn btn-sm btn-alt-secondary"
              :aria-expanded="chartOpen.publish ? 'true' : 'false'"
              aria-controls="daily-report-chart-publish"
              @click="toggleChart('publish')"
            >
              <i
                class="fa fa-chart-simple me-1"
                aria-hidden="true"
              /> Chart
              <i
                class="fa"
                :class="chartOpen.publish ? 'fa-angle-up' : 'fa-angle-down'"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div class="block-content block-content-full">
          <div
            v-if="showCharts && chartOpen.publish"
            id="daily-report-chart-publish"
            class="border-bottom mb-3 pb-3"
          >
            <report-chart
              type="stacked"
              :series="publishChart"
              :target="PUBLISH_MIN"
              target-label="min"
              empty-text="Enter publish numbers to see the chart."
            />
          </div>
          <div class="table-responsive">
            <table class="table table-sm table-vcenter mb-0">
              <thead class="table-light">
                <tr>
                  <th style="min-width: 200px;">
                    Staff
                  </th>
                  <th
                    class="text-center"
                    style="width: 110px;"
                  >
                    Vetted
                  </th>
                  <th
                    class="text-center"
                    style="width: 110px;"
                  >
                    Semi-vetted
                  </th>
                  <th
                    class="text-center"
                    style="width: 80px;"
                  >
                    Total
                  </th>
                  <th
                    class="text-center"
                    style="width: 120px;"
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in publishRows"
                  :id="'row-publish-' + row.staff_id"
                  :key="'p' + row.key"
                  :class="{ 'table-warning': rowWarnings('publish', row.staff_id).length }"
                >
                  <td class="fw-semibold">
                    {{ staffName(row.staff_id) }}<span
                      v-if="isManager(row.staff_id)"
                      class="fw-normal text-muted"
                    > (Manager)</span>
                    <i
                      v-if="rowWarnings('publish', row.staff_id).length"
                      class="fa fa-triangle-exclamation ms-1"
                      :title="rowWarningTitle('publish', row.staff_id)"
                      aria-hidden="true"
                    />
                  </td>
                  <td>
                    <input
                      v-model.number="row.vetted"
                      type="number"
                      min="0"
                      step="1"
                      class="form-control form-control-sm text-center"
                    >
                  </td>
                  <td>
                    <input
                      v-model.number="row.semi_vetted"
                      type="number"
                      min="0"
                      step="1"
                      class="form-control form-control-sm text-center"
                    >
                  </td>
                  <td class="text-center fw-semibold">
                    {{ publishTotal(row) }}
                  </td>
                  <td class="text-center">
                    <span
                      class="badge"
                      :class="publishTotal(row) >= PUBLISH_MIN ? 'bg-success' : 'bg-danger'"
                    >
                      {{ publishTotal(row) >= PUBLISH_MIN ? 'On target' : 'Below ' + PUBLISH_MIN }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!publishRows.length">
                  <td
                    colspan="5"
                    class="text-center text-muted py-3"
                  >
                    No staff assigned to this manager.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 2. New projects past 3 days -->
      <div class="block block-rounded">
        <div class="block-header border-bottom">
          <div>
            <h3 class="block-title">
              2. New projects published — past 3 days
            </h3>
            <p class="text-muted fs-sm mb-0">
              Minimum {{ PROJECT_MIN }} published per project
            </p>
          </div>
          <div class="block-options d-flex align-items-center gap-2">
            <span
              class="badge"
              :class="projectsBelowCount ? 'bg-danger' : 'bg-success'"
            >{{ projectsBelowCount }} of {{ filledProjectCount }} below minimum</span>
            <button
              v-if="showCharts"
              type="button"
              class="btn btn-sm btn-alt-secondary"
              :aria-expanded="chartOpen.projects ? 'true' : 'false'"
              aria-controls="daily-report-chart-projects"
              @click="toggleChart('projects')"
            >
              <i
                class="fa fa-chart-simple me-1"
                aria-hidden="true"
              /> Chart
              <i
                class="fa"
                :class="chartOpen.projects ? 'fa-angle-up' : 'fa-angle-down'"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div class="block-content block-content-full">
          <div
            v-if="showCharts && chartOpen.projects"
            id="daily-report-chart-projects"
            class="border-bottom mb-3 pb-3"
          >
            <report-chart
              type="bar"
              :series="projectsChart"
              :target="PROJECT_MIN"
              target-label="min"
              empty-text="Enter project publish numbers to see the chart."
            />
          </div>
          <div class="table-responsive">
            <table class="table table-sm table-vcenter mb-2">
              <thead class="table-light">
                <tr>
                  <th style="width: 170px;">
                    Date
                  </th>
                  <th style="min-width: 260px;">
                    Project
                  </th>
                  <th
                    class="text-center"
                    style="width: 110px;"
                  >
                    Published
                  </th>
                  <th
                    class="text-center"
                    style="width: 120px;"
                  >
                    Status
                  </th>
                  <th style="width: 50px;" />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in projectRows"
                  :key="'j' + row.key"
                >
                  <td>
                    <input
                      v-model="row.date"
                      type="date"
                      class="form-control form-control-sm"
                      :class="{ 'is-invalid': submitted && !row.date && !isBlankProject(row) }"
                    >
                  </td>
                  <td>
                    <project-select
                      v-model="row.project_id"
                      :text="row.name"
                      :url="projectOptionsUrl"
                      :manager-staff-id="managerStaffId"
                      :disabled="readonly"
                      :invalid="(submitted && !row.name && !isBlankProject(row)) || isDuplicateProject(row)"
                      @select="row.name = $event.text"
                    />
                  </td>
                  <td>
                    <input
                      v-model.number="row.published"
                      type="number"
                      min="0"
                      step="1"
                      class="form-control form-control-sm text-center"
                    >
                  </td>
                  <td class="text-center">
                    <span
                      class="badge"
                      :class="num(row.published) >= PROJECT_MIN ? 'bg-success' : 'bg-danger'"
                    >
                      {{ num(row.published) >= PROJECT_MIN ? 'On target' : 'Below ' + PROJECT_MIN }}
                    </span>
                  </td>
                  <td class="text-center">
                    <button
                      type="button"
                      class="btn btn-sm btn-alt-danger"
                      title="Remove"
                      @click="removeRow(projectRows, i)"
                    >
                      <i class="fa fa-times" />
                    </button>
                  </td>
                </tr>
                <tr v-if="!projectRows.length">
                  <td
                    colspan="5"
                    class="text-center text-muted py-3"
                  >
                    No projects added. Leave empty if there were none.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-alt-primary"
            @click="addProjectRow"
          >
            <i class="fa fa-plus me-1" /> Add project
          </button>
        </div>
      </div>

      <!-- 3. Active projects per person -->
      <div class="block block-rounded">
        <div class="block-header border-bottom">
          <div>
            <h3 class="block-title">
              3. Active projects per person
            </h3>
            <p class="text-muted fs-sm mb-0">
              Team average target is {{ ACTIVE_AVG }}
            </p>
          </div>
          <div class="block-options d-flex align-items-center gap-2">
            <span
              class="badge"
              :class="activeAverage >= ACTIVE_AVG ? 'bg-success' : 'bg-warning'"
            >Team avg {{ activeAverage }}</span>
            <button
              v-if="showCharts"
              type="button"
              class="btn btn-sm btn-alt-secondary"
              :aria-expanded="chartOpen.active ? 'true' : 'false'"
              aria-controls="daily-report-chart-active"
              @click="toggleChart('active')"
            >
              <i
                class="fa fa-chart-simple me-1"
                aria-hidden="true"
              /> Chart
              <i
                class="fa"
                :class="chartOpen.active ? 'fa-angle-up' : 'fa-angle-down'"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div class="block-content block-content-full">
          <div
            v-if="showCharts && chartOpen.active"
            id="daily-report-chart-active"
            class="border-bottom mb-3 pb-3"
          >
            <report-chart
              type="bar"
              :series="activeChart"
              :target="ACTIVE_AVG"
              target-label="avg"
              empty-text="Enter active project counts to see the chart."
            />
          </div>
          <div class="table-responsive">
            <table class="table table-sm table-vcenter mb-0">
              <thead class="table-light">
                <tr>
                  <th style="min-width: 200px;">
                    Staff
                  </th>
                  <th
                    class="text-center"
                    style="width: 130px;"
                  >
                    Active projects
                  </th>
                  <th
                    class="text-center"
                    style="width: 130px;"
                  >
                    vs. average
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in activeRows"
                  :id="'row-active-' + row.staff_id"
                  :key="'a' + row.key"
                  :class="{ 'table-warning': rowWarnings('active', row.staff_id).length }"
                >
                  <td class="fw-semibold">
                    {{ staffName(row.staff_id) }}<span
                      v-if="isManager(row.staff_id)"
                      class="fw-normal text-muted"
                    > (Manager)</span>
                    <i
                      v-if="rowWarnings('active', row.staff_id).length"
                      class="fa fa-triangle-exclamation ms-1"
                      :title="rowWarningTitle('active', row.staff_id)"
                      aria-hidden="true"
                    />
                  </td>
                  <td>
                    <input
                      v-model.number="row.count"
                      type="number"
                      min="0"
                      step="1"
                      class="form-control form-control-sm text-center"
                    >
                  </td>
                  <td
                    class="text-center fw-semibold"
                    :class="activeDelta(row) >= 0 ? 'text-success' : 'text-warning'"
                  >
                    {{ activeDelta(row) >= 0 ? '+' : '' }}{{ activeDelta(row) }}
                  </td>
                </tr>
                <tr v-if="!activeRows.length">
                  <td
                    colspan="3"
                    class="text-center text-muted py-3"
                  >
                    No staff assigned to this manager.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 4. Recruitment update -->
      <div class="block block-rounded">
        <div class="block-header border-bottom">
          <div>
            <h3 class="block-title">
              4. Recruitment update
            </h3>
            <p class="text-muted fs-sm mb-0">
              Notes on open roles and hiring progress
            </p>
          </div>
        </div>
        <div class="block-content block-content-full">
          <label
            class="form-label fs-sm mb-1"
            for="daily-report-recruitment"
          >Update</label>
          <textarea
            id="daily-report-recruitment"
            v-model.trim="recruitmentText"
            rows="6"
            class="form-control form-control-sm"
            placeholder="Open roles, what happened, what's pending, next steps"
          />
        </div>
      </div>

      <!-- 5. Credits -->
      <div class="block block-rounded">
        <div class="block-header border-bottom">
          <div>
            <h3 class="block-title">
              5. Credits &amp; monthly target
            </h3>
            <p class="text-muted fs-sm mb-0">
              Average target {{ CREDIT_TARGET }} credits per month
            </p>
          </div>
          <div class="block-options d-flex align-items-center gap-2">
            <span class="badge bg-primary">
              {{ creditTotals.completed }} completed · {{ creditTotals.scheduled }} scheduled · {{ creditTotals.requested }} requested
            </span>
            <button
              v-if="showCharts"
              type="button"
              class="btn btn-sm btn-alt-secondary"
              :aria-expanded="chartOpen.credits ? 'true' : 'false'"
              aria-controls="daily-report-chart-credits"
              @click="toggleChart('credits')"
            >
              <i
                class="fa fa-chart-simple me-1"
                aria-hidden="true"
              /> Chart
              <i
                class="fa"
                :class="chartOpen.credits ? 'fa-angle-up' : 'fa-angle-down'"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div class="block-content block-content-full">
          <div
            v-if="showCharts && chartOpen.credits"
            id="daily-report-chart-credits"
            class="border-bottom mb-3 pb-3"
          >
            <report-chart
              type="stacked"
              :series="creditsChart"
              :target="creditsChartTarget"
              target-label="avg"
              empty-text="Enter credit numbers to see the chart."
            />
          </div>
          <div class="table-responsive">
            <table class="table table-sm table-vcenter mb-0">
              <thead class="table-light">
                <tr>
                  <th style="min-width: 200px;">
                    Staff
                  </th>
                  <th
                    class="text-center"
                    style="width: 100px;"
                  >
                    Completed
                  </th>
                  <th
                    class="text-center"
                    style="width: 100px;"
                  >
                    Scheduled
                  </th>
                  <th
                    class="text-center"
                    style="width: 100px;"
                  >
                    Requested
                  </th>
                  <th
                    class="text-center"
                    style="width: 100px;"
                  >
                    Target
                  </th>
                  <th style="min-width: 160px;">
                    Progress
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in creditRows"
                  :id="'row-credits-' + row.staff_id"
                  :key="'c' + row.key"
                  :class="{ 'table-warning': rowWarnings('credits', row.staff_id).length }"
                >
                  <td class="fw-semibold">
                    {{ staffName(row.staff_id) }}<span
                      v-if="isManager(row.staff_id)"
                      class="fw-normal text-muted"
                    > (Manager)</span>
                    <i
                      v-if="rowWarnings('credits', row.staff_id).length"
                      class="fa fa-triangle-exclamation ms-1"
                      :title="rowWarningTitle('credits', row.staff_id)"
                      aria-hidden="true"
                    />
                  </td>
                  <td>
                    <input
                      v-model.number="row.completed"
                      type="number"
                      min="0"
                      step="0.5"
                      class="form-control form-control-sm text-center"
                    >
                  </td>
                  <td>
                    <input
                      v-model.number="row.scheduled"
                      type="number"
                      min="0"
                      step="0.5"
                      class="form-control form-control-sm text-center"
                    >
                  </td>
                  <td>
                    <input
                      v-model.number="row.requested"
                      type="number"
                      min="0"
                      step="0.5"
                      class="form-control form-control-sm text-center"
                    >
                  </td>
                  <td>
                    <input
                      v-model.number="row.target"
                      type="number"
                      min="0"
                      step="1"
                      class="form-control form-control-sm text-center"
                    >
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <div
                        class="progress flex-grow-1"
                        style="height: 8px;"
                      >
                        <div
                          class="progress-bar"
                          :class="creditPercent(row) >= 100 ? 'bg-success' : 'bg-primary'"
                          role="progressbar"
                          :style="{ width: Math.min(creditPercent(row), 100) + '%' }"
                          :aria-valuenow="creditPercent(row)"
                          aria-valuemin="0"
                          aria-valuemax="100"
                        />
                      </div>
                      <span
                        class="fs-sm text-muted"
                        style="width: 40px;"
                      >{{ creditPercent(row) }}%</span>
                    </div>
                  </td>
                </tr>
                <tr v-if="!creditRows.length">
                  <td
                    colspan="6"
                    class="text-center text-muted py-3"
                  >
                    No staff assigned to this manager.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </fieldset>

    <!-- Sticky action bar -->
    <div class="daily-report-actions block block-rounded mb-0">
      <div class="block-content py-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div class="fs-sm">
          <span
            v-if="submitted && validationErrors.length"
            class="text-danger"
          >
            <i class="fa fa-exclamation-circle me-1" />{{ validationErrors[0] }}
          </span>
          <span
            v-else
            class="text-muted"
          >
            <span
              v-if="!readonly"
              class="badge me-1"
              :class="initialReport ? 'text-bg-warning' : 'text-bg-info'"
            >{{ initialReport ? 'Updating' : 'New' }}</span>
            Report for <strong>{{ reportDate || '—' }}</strong>
          </span>
        </div>
        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-sm btn-alt-secondary"
            @click="openPreview"
          >
            <i class="fa fa-eye me-1" /> Preview
          </button>
          <button
            v-if="!readonly"
            type="button"
            class="btn btn-sm btn-alt-secondary"
            :disabled="saving"
            @click="submit('draft')"
          >
            <i class="fa fa-save me-1" /> Save draft
          </button>
          <button
            v-if="!readonly"
            type="button"
            class="btn btn-sm btn-primary"
            :disabled="saving"
            @click="submit('submitted')"
          >
            <i
              v-if="saving"
              class="fa fa-spinner fa-spin me-1"
            />
            <i
              v-else
              class="fa fa-paper-plane me-1"
            /> Submit report
          </button>
        </div>
      </div>
    </div>

    <!-- Preview modal -->
    <div
      v-if="showPreview"
      class="modal d-block"
      tabindex="-1"
      role="dialog"
      @click.self="showPreview = false"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="block block-rounded mb-0">
            <div class="block-header block-header-default">
              <h3 class="block-title">
                Report preview · {{ reportDate }}<span v-if="managerName"> · {{ managerName }}</span>
              </h3>
              <div class="block-options">
                <button
                  type="button"
                  class="btn-block-option"
                  @click="showPreview = false"
                >
                  <i class="fa fa-times" />
                </button>
              </div>
            </div>
            <div class="block-content">
              <pre class="daily-report-preview mb-0">{{ previewText }}</pre>
            </div>
            <div class="block-content block-content-full text-end bg-body-light">
              <button
                type="button"
                class="btn btn-sm btn-alt-secondary me-1"
                @click="showPreview = false"
              >
                Close
              </button>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click="copyPreview"
              >
                <i
                  class="fa me-1"
                  :class="copied ? 'fa-check' : 'fa-copy'"
                /> {{ copied ? 'Copied' : 'Copy text' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showPreview"
      class="modal-backdrop show"
    />

    <div
      v-if="showWarnings"
      class="modal d-block"
      tabindex="-1"
      role="dialog"
      aria-labelledby="daily-report-warnings-title"
      @click.self="showWarnings = false"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="block block-rounded mb-0">
            <div class="block-header block-header-default">
              <h3
                id="daily-report-warnings-title"
                class="block-title"
              >
                Check these before submitting
              </h3>
              <div class="block-options">
                <button
                  type="button"
                  class="btn-block-option"
                  @click="showWarnings = false"
                >
                  <i class="fa fa-times" />
                </button>
              </div>
            </div>
            <div class="block-content">
              <p class="text-muted fs-sm">
                These look like possible typos. Fix the row, or submit anyway.
              </p>
              <ul class="list-unstyled mb-0">
                <li
                  v-for="(warning, index) in warnings"
                  :key="warning.rowKey + '-' + index"
                  class="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2 border-bottom"
                >
                  <span>{{ warning.message }}</span>
                  <button
                    type="button"
                    class="btn btn-sm btn-link"
                    @click="goToWarning(warning)"
                  >
                    Go to row
                  </button>
                </li>
              </ul>
            </div>
            <div class="block-content block-content-full text-end bg-body-light">
              <button
                type="button"
                class="btn btn-sm btn-alt-secondary me-1"
                @click="showWarnings = false"
              >
                Review
              </button>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                :disabled="saving"
                @click="confirmWarnings"
              >
                Submit anyway
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showWarnings"
      class="modal-backdrop show"
    />
  </div>
</template>

<script>
/**
 * Daily report entry form (UI phase). Nothing is persisted yet; `payload` is the
 * JSON the backend phase should accept unchanged:
 *
 * {
 *   report_date: "YYYY-MM-DD",
 *   publish_per_person: [{ staff_id, vetted, semi_vetted }],
 *   new_projects:       [{ project_id, date, name, published }],
 *   active_projects:    [{ staff_id, count }],
 *   recruitment:        "free-text notes",
 *   credits:            [{ staff_id, completed, scheduled, requested, target }],
 * }
 */
import ProjectSelect from "./ProjectSelect.vue";

const PUBLISH_MIN = 3;
const PROJECT_MIN = 3;
const ACTIVE_AVG = 7;
const ACTIVE_JUMP = 3;
const CREDIT_TARGET = 15;
const CREDIT_OVER_FACTOR = 2;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];

let keySeq = 0;

export default {
    name: "DailyReportForm",
    components: { ProjectSelect },
    props: {
        staffOptions: {
            type: Array,
            default: () => [],
        },
        initialDate: {
            type: String,
            default: "",
        },
        managerName: {
            type: String,
            default: "",
        },
        backUrl: {
            type: String,
            default: "",
        },
        showCharts: {
            type: Boolean,
            default: false,
        },
        storeUrl: {
            type: String,
            default: "",
        },
        // Select2 AJAX source for the project field (manager + team's projects).
        projectOptionsUrl: {
            type: String,
            default: "",
        },
        managerStaffId: {
            type: [Number, String],
            default: null,
        },
        // A filed report, in the same shape as `payload`. Present on the detail page,
        // and on the create page when today's report already exists.
        initialReport: {
            type: Object,
            default: null,
        },
        previousReport: {
            type: Object,
            default: null,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        backLabel: {
            type: String,
            default: "",
        },
        // Form page for this manager; `?date=` is appended to switch days.
        createUrl: {
            type: String,
            default: "",
        },
        chartsOpen: {
            type: Boolean,
            default: true,
        },
        // { status, submitted_at, updated_at, creator, on_behalf } of a filed report.
        meta: {
            type: Object,
            default: null,
        },
    },
    data() {
        // A filed report is shown against the thresholds it was saved with.
        const saved = (this.initialReport && this.initialReport.thresholds) || {};
        return {
            PUBLISH_MIN: saved.publish_min ?? PUBLISH_MIN,
            PROJECT_MIN: saved.project_min ?? PROJECT_MIN,
            ACTIVE_AVG: saved.active_avg ?? ACTIVE_AVG,
            CREDIT_TARGET: saved.credit_target ?? CREDIT_TARGET,
            todayIso: this.toIso(new Date()),
            reportDate: this.initialDate || this.toIso(new Date()),
            chartOpen: {
                publish: this.chartsOpen,
                projects: this.chartsOpen,
                active: this.chartsOpen,
                credits: this.chartsOpen,
            },
            savedSnapshot: "",
            leaving: false,
            publishRows: [],
            projectRows: [],
            activeRows: [],
            recruitmentText: "",
            creditRows: [],
            submitted: false,
            saving: false,
            showPreview: false,
            showWarnings: false,
            warningsConfirmed: false,
            copied: false,
        };
    },
    computed: {
        yesterdayIso() {
            if (!this.reportDate) return "";
            const d = new Date(this.reportDate + "T00:00:00");
            d.setDate(d.getDate() - 1);
            return this.toIso(d);
        },
        yesterdayLabel() {
            return this.yesterdayIso ? "Yesterday, " + this.fmtDate(this.yesterdayIso) : "Yesterday";
        },
        metaLine() {
            if (!this.meta) return "";
            const by = this.meta.creator ? ` by ${this.meta.creator}${this.meta.on_behalf ? " (on behalf)" : ""}` : "";
            return this.meta.status === "submitted"
                ? `Submitted ${this.meta.submitted_at || ""}${by}`
                : `Draft · last saved ${this.meta.updated_at || ""}${by}`;
        },
        isDirty() {
            return !this.readonly && JSON.stringify(this.payload) !== this.savedSnapshot;
        },
        publishMetCount() {
            return this.publishRows.filter((r) => this.publishTotal(r) >= this.PUBLISH_MIN).length;
        },
        projectsBelowCount() {
            return this.projectRows.filter((r) => !this.isBlankProject(r) && this.num(r.published) < this.PROJECT_MIN).length;
        },
        filledProjectCount() {
            return this.projectRows.filter((r) => !this.isBlankProject(r)).length;
        },
        activeAverage() {
            if (!this.activeRows.length) return 0;
            const sum = this.activeRows.reduce((acc, r) => acc + this.num(r.count), 0);
            return Math.round((sum / this.activeRows.length) * 10) / 10;
        },
        creditTotals() {
            return this.creditRows.reduce(
                (acc, r) => ({
                    completed: acc.completed + this.num(r.completed),
                    scheduled: acc.scheduled + this.num(r.scheduled),
                    requested: acc.requested + this.num(r.requested),
                    target: acc.target + this.num(r.target),
                }),
                { completed: 0, scheduled: 0, requested: 0, target: 0 }
            );
        },
        publishChart() {
            return this.publishRows
                .filter((r) => this.publishTotal(r) > 0)
                .map((r) => {
                    const total = this.publishTotal(r);
                    const flagged = total < this.PUBLISH_MIN;
                    return {
                        label: this.staffChartLabel(r.staff_id),
                        value: total,
                        flagged,
                        color: flagged ? "danger" : "success",
                        hint: flagged ? "below minimum" : "",
                        segments: [
                            { value: this.num(r.vetted), class: flagged ? "fill-danger" : "fill-success" },
                            { value: this.num(r.semi_vetted), class: flagged ? "fill-warning" : "fill-primary" },
                        ],
                    };
                });
        },
        projectsChart() {
            return this.projectRows
                .filter((r) => this.num(r.published) > 0 || r.name)
                .map((r) => {
                    const published = this.num(r.published);
                    const flagged = published < this.PROJECT_MIN;
                    return {
                        label: `${this.fmtDate(r.date)} ${r.name || "(untitled)"}`,
                        value: published,
                        flagged,
                        color: flagged ? "danger" : "success",
                        hint: flagged ? "below minimum" : "",
                    };
                });
        },
        activeChart() {
            return this.activeRows
                .filter((r) => this.num(r.count) > 0)
                .map((r) => {
                    const count = this.num(r.count);
                    const flagged = count !== this.ACTIVE_AVG;
                    return {
                        label: this.staffChartLabel(r.staff_id),
                        value: count,
                        flagged,
                        color: flagged ? "warning" : "success",
                        hint: flagged ? "off average" : "",
                    };
                });
        },
        creditsChart() {
            return this.creditRows
                .filter((r) => this.num(r.completed) || this.num(r.scheduled) || this.num(r.requested))
                .map((r) => {
                    const flagged = this.num(r.target) > 0 && this.creditPercent(r) < 100;
                    return {
                        label: this.staffChartLabel(r.staff_id),
                        value: this.num(r.completed) + this.num(r.scheduled) + this.num(r.requested),
                        flagged,
                        color: flagged ? "danger" : "success",
                        hint: flagged ? "behind target" : "",
                        segments: [
                            { value: this.num(r.completed), class: "fill-success" },
                            { value: this.num(r.scheduled), class: "fill-primary" },
                            { value: this.num(r.requested), class: "fill-warning" },
                        ],
                    };
                });
        },
        creditsChartTarget() {
            const targets = this.creditRows
                .filter((r) => this.num(r.completed) || this.num(r.scheduled) || this.num(r.requested))
                .map((r) => this.num(r.target))
                .filter((t) => t > 0);
            if (!targets.length) return 0;
            const first = targets[0];
            return targets.every((t) => t === first) ? first : 0;
        },
        validationErrors() {
            const errors = [];
            if (!this.reportDate) errors.push("Report date is required.");
            if (this.publishRows.some((r) => !r.staff_id)) errors.push("Select a staff member for every publish row.");
            if (this.projectRows.some((r) => !this.isBlankProject(r) && (!r.name || !r.date))) errors.push("Every project needs a date and a selected project.");
            if (this.projectRows.some((r) => this.isDuplicateProject(r))) errors.push("Each project can only be listed once.");
            if (this.activeRows.some((r) => !r.staff_id)) errors.push("Select a staff member for every active-project row.");
            if (this.creditRows.some((r) => !r.staff_id)) errors.push("Select a staff member for every credit row.");
            return errors;
        },
        payload() {
            return {
                report_date: this.reportDate,
                publish_per_person: this.publishRows.map((r) => ({
                    staff_id: r.staff_id,
                    vetted: this.num(r.vetted),
                    semi_vetted: this.num(r.semi_vetted),
                })),
                // An untouched project row (no name, nothing published) is dropped, not rejected.
                new_projects: this.projectRows.filter((r) => !this.isBlankProject(r)).map((r) => ({
                    project_id: r.project_id,
                    date: r.date,
                    name: r.name,
                    published: this.num(r.published),
                })),
                active_projects: this.activeRows.map((r) => ({
                    staff_id: r.staff_id,
                    count: this.num(r.count),
                })),
                recruitment: this.recruitmentText,
                credits: this.creditRows.map((r) => ({
                    staff_id: r.staff_id,
                    completed: this.num(r.completed),
                    scheduled: this.num(r.scheduled),
                    requested: this.num(r.requested),
                    target: this.num(r.target),
                })),
            };
        },
        previewText() {
            const lines = [];

            lines.push(`Yesterday publish number per person (minimum ${this.PUBLISH_MIN}/day)`);
            this.publishRows.forEach((r) => {
                const parts = [];
                if (this.num(r.vetted)) parts.push(`${this.num(r.vetted)} Vetted`);
                if (this.num(r.semi_vetted)) parts.push(`${this.num(r.semi_vetted)} Semi-Vetted`);
                lines.push(`${this.staffName(r.staff_id)} ${parts.length ? parts.join(" ") : "0"}`);
            });

            lines.push("");
            lines.push(`Past 3 days new project publish number (minimum ${this.PROJECT_MIN} per project)`);
            this.projectRows.forEach((r) => {
                lines.push(`${this.fmtDate(r.date)} - ${r.name || "(untitled)"} - ${this.num(r.published)} published`);
            });

            lines.push("");
            lines.push(`Active project per person is handling (average is ${this.ACTIVE_AVG})`);
            this.activeRows.forEach((r) => {
                lines.push(`${this.staffName(r.staff_id)} ${this.num(r.count)}`);
            });

            lines.push("");
            lines.push("Recruitment update");
            if (this.recruitmentText) {
                lines.push(this.recruitmentText);
            }

            lines.push("");
            lines.push(`Current credit per person and what's each person's monthly target (average target should be ${this.CREDIT_TARGET} credits/month)`);
            this.creditRows.forEach((r) => {
                lines.push(
                    `${this.staffName(r.staff_id)}: ${this.num(r.completed)} completed | ${this.num(r.scheduled)} scheduled | ${this.num(r.requested)} requested (target ${this.num(r.target)})`
                );
            });

            return lines.join("\n");
        },
        prevByStaff() {
            const map = {};
            const prev = this.previousReport;
            if (!prev) return map;

            (prev.active_projects || []).forEach((row) => {
                if (!map[row.staff_id]) map[row.staff_id] = {};
                map[row.staff_id].active = this.num(row.count);
            });
            (prev.credits || []).forEach((row) => {
                if (!map[row.staff_id]) map[row.staff_id] = {};
                map[row.staff_id].credits = {
                    completed: this.num(row.completed),
                    target: this.num(row.target),
                };
            });

            return map;
        },
        warnings() {
            if (this.readonly) return [];

            const list = [];
            const prevDate = this.previousReport && this.previousReport.report_date;
            const sameMonth = !!(prevDate && this.reportDate && prevDate.slice(0, 7) === this.reportDate.slice(0, 7));
            const since = prevDate ? this.fmtDate(prevDate) : "";
            const push = (section, staffId, message, span = false) => {
                list.push({
                    section,
                    staffId,
                    rowKey: `row-${section}-${staffId}`,
                    message,
                    span,
                });
            };

            this.publishRows.forEach((row) => {
                if (!row.staff_id || !this.personIsEmpty(row.staff_id)) return;
                push("publish", row.staff_id, `${this.staffName(row.staff_id)}: nothing entered in any section`, true);
            });

            this.activeRows.forEach((row) => {
                if (!row.staff_id) return;
                const earlier = this.prevByStaff[row.staff_id];
                if (!earlier || earlier.active === undefined) return;
                const count = this.num(row.count);
                if (Math.abs(count - earlier.active) < ACTIVE_JUMP) return;
                push("active", row.staff_id, `${this.staffName(row.staff_id)}: active projects ${earlier.active} → ${count}`);
            });

            this.creditRows.forEach((row) => {
                if (!row.staff_id) return;
                const name = this.staffName(row.staff_id);
                const earlier = this.prevByStaff[row.staff_id];
                const completed = this.num(row.completed);
                const target = this.num(row.target);
                const prevCredits = earlier && earlier.credits;

                if (sameMonth && prevCredits && completed < prevCredits.completed) {
                    push("credits", row.staff_id, `${name}: completed credits dropped ${prevCredits.completed} → ${completed} since ${since}`);
                }
                if (prevCredits && target !== prevCredits.target) {
                    push("credits", row.staff_id, `${name}: monthly target changed ${prevCredits.target} → ${target}`);
                }
                if (completed > CREDIT_OVER_FACTOR * target) {
                    push("credits", row.staff_id, `${name}: ${completed} completed vs target ${target}`);
                }
            });

            return list;
        },
    },
    watch: {
        payload() {
            this.warningsConfirmed = false;
        },
    },
    created() {
        // A filed report fills the roster rows; the roster itself always comes from staffOptions.
        if (this.initialReport) {
            this.hydrate(this.initialReport);
            return;
        }

        // Roster-based sections start pre-filled with every staff member.
        this.staffOptions.forEach((s) => {
            this.publishRows.push(this.newPublishRow(s.id));
            this.activeRows.push(this.newActiveRow(s.id));
            this.creditRows.push(this.newCreditRow(s.id));
        });
    },
    mounted() {
        this.savedSnapshot = JSON.stringify(this.payload);
        window.addEventListener("beforeunload", this.warnUnsaved);
    },
    beforeDestroy() {
        window.removeEventListener("beforeunload", this.warnUnsaved);
    },
    methods: {
        warnUnsaved(event) {
            if (this.leaving || !this.isDirty) return;
            event.preventDefault();
            event.returnValue = "";
        },
        goToDate() {
            if (!this.createUrl || !this.reportDate || this.reportDate > this.todayIso) return;
            const sep = this.createUrl.includes("?") ? "&" : "?";
            window.location = `${this.createUrl}${sep}date=${encodeURIComponent(this.reportDate)}`;
        },
        isBlankProject(row) {
            return !row.name && !this.num(row.published);
        },
        // Another row already holds the same project.
        isDuplicateProject(row) {
            return !!row.project_id && this.projectRows.some((r) => r !== row && r.project_id === row.project_id);
        },
        fmtLongDate(iso) {
            if (!iso) return "—";
            return iso === this.todayIso ? `today (${this.fmtDate(iso)})` : this.fmtDate(iso);
        },
        toIso(d) {
            const pad = (n) => String(n).padStart(2, "0");
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        },
        fmtDate(iso) {
            if (!iso) return "(no date)";
            const d = new Date(iso + "T00:00:00");
            if (Number.isNaN(d.getTime())) return iso;
            const day = d.getDate();
            const suffix = day % 10 === 1 && day !== 11 ? "st" : day % 10 === 2 && day !== 12 ? "nd" : day % 10 === 3 && day !== 13 ? "rd" : "th";
            return `${MONTHS[d.getMonth()]} ${day}${suffix}`;
        },
        num(v) {
            const n = Number(v);
            return Number.isFinite(n) && n > 0 ? n : 0;
        },
        staffName(id) {
            const s = this.staffOptions.find((o) => o.id === id);
            return s ? s.name : "(unassigned)";
        },
        isManager(staffId) {
            if (this.managerStaffId === null || this.managerStaffId === "") {
                return false;
            }
            return Number(staffId) === Number(this.managerStaffId);
        },
        staffChartLabel(id) {
            const name = this.staffName(id);
            return this.isManager(id) ? `${name} (Manager)` : name;
        },
        personIsEmpty(staffId) {
            const publish = this.publishRows.find((r) => r.staff_id === staffId);
            const active = this.activeRows.find((r) => r.staff_id === staffId);
            const credit = this.creditRows.find((r) => r.staff_id === staffId);
            const fields = [];

            if (publish) fields.push(this.num(publish.vetted), this.num(publish.semi_vetted));
            if (active) fields.push(this.num(active.count));
            if (credit) {
                fields.push(this.num(credit.completed), this.num(credit.scheduled), this.num(credit.requested), this.num(credit.target));
            }

            return fields.length > 0 && fields.every((n) => n === 0);
        },
        rowWarnings(section, staffId) {
            return this.warnings.filter((w) => w.staffId === staffId && (w.section === section || w.span));
        },
        rowWarningTitle(section, staffId) {
            return this.rowWarnings(section, staffId).map((w) => w.message).join("\n");
        },
        goToWarning(warning) {
            if (!warning) return;
            this.showWarnings = false;
            this.$nextTick(() => {
                const el = document.getElementById(warning.rowKey);
                if (!el) return;
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                const input = el.querySelector("input");
                if (input) input.focus({ preventScroll: true });
            });
        },
        confirmWarnings() {
            this.warningsConfirmed = true;
            this.showWarnings = false;
            this.submit("submitted");
        },
        publishTotal(row) {
            return this.num(row.vetted) + this.num(row.semi_vetted);
        },
        activeDelta(row) {
            return this.num(row.count) - this.ACTIVE_AVG;
        },
        creditPercent(row) {
            const target = this.num(row.target);
            if (!target) return 0;
            return Math.round((this.num(row.completed) / target) * 100);
        },
        newPublishRow(staffId = null) {
            return { key: ++keySeq, staff_id: staffId, vetted: 0, semi_vetted: 0 };
        },
        newProjectRow() {
            return { key: ++keySeq, project_id: null, date: this.yesterdayIso, name: "", published: 0 };
        },
        newActiveRow(staffId = null) {
            return { key: ++keySeq, staff_id: staffId, count: 0 };
        },
        newCreditRow(staffId = null) {
            return { key: ++keySeq, staff_id: staffId, completed: 0, scheduled: 0, requested: 0, target: this.CREDIT_TARGET };
        },
        addProjectRow() {
            this.projectRows.push(this.newProjectRow());
        },
        removeRow(rows, index) {
            rows.splice(index, 1);
        },
        toggleChart(key) {
            this.chartOpen[key] = !this.chartOpen[key];
        },
        openPreview() {
            this.copied = false;
            this.showPreview = true;
        },
        copyPreview() {
            const done = () => {
                this.copied = true;
                setTimeout(() => (this.copied = false), 2000);
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(this.previewText).then(done).catch(() => this.fallbackCopy(done));
            } else {
                this.fallbackCopy(done);
            }
        },
        fallbackCopy(done) {
            const ta = document.createElement("textarea");
            ta.value = this.previewText;
            ta.setAttribute("readonly", "");
            ta.style.position = "absolute";
            ta.style.left = "-9999px";
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand("copy");
                done();
            } finally {
                document.body.removeChild(ta);
            }
        },
        notify(type, message) {
            if (window.One && typeof window.One.helpers === "function") {
                const icon = type === "success" ? "fa fa-check me-1" : "fa fa-exclamation-circle me-1";
                window.One.helpers("jq-notify", { type, icon, message });
            } else {
                window.alert(message);
            }
        },
        /**
         * Roster section rows: one row per staff option, carrying the saved values when the
         * report has them. A saved row for someone no longer on the roster is kept at the end
         * so nothing filed earlier disappears.
         */
        mergeRoster(saved, factory, fill) {
            const bySaved = new Map((saved || []).map((r) => [r.staff_id, r]));
            const rows = this.staffOptions.map((s) => {
                const row = factory(s.id);
                const match = bySaved.get(s.id);
                if (match) {
                    bySaved.delete(s.id);
                    fill(row, match);
                }
                return row;
            });
            bySaved.forEach((match) => {
                const row = factory(match.staff_id);
                fill(row, match);
                rows.push(row);
            });
            return rows;
        },
        hydrate(report) {
            this.reportDate = report.report_date || this.reportDate;
            this.recruitmentText = report.recruitment || "";
            this.publishRows = this.mergeRoster(
                report.publish_per_person,
                (id) => this.newPublishRow(id),
                (row, saved) => {
                    row.vetted = saved.vetted;
                    row.semi_vetted = saved.semi_vetted;
                }
            );
            this.projectRows = (report.new_projects || []).map((r) => ({
                key: ++keySeq,
                project_id: r.project_id ?? null,
                date: r.date,
                name: r.name,
                published: r.published,
            }));
            this.activeRows = this.mergeRoster(
                report.active_projects,
                (id) => this.newActiveRow(id),
                (row, saved) => {
                    row.count = saved.count;
                }
            );
            this.creditRows = this.mergeRoster(
                report.credits,
                (id) => this.newCreditRow(id),
                (row, saved) => {
                    row.completed = saved.completed;
                    row.scheduled = saved.scheduled;
                    row.requested = saved.requested;
                    row.target = saved.target;
                }
            );
        },
        submit(status = "submitted") {
            this.submitted = true;
            if (this.validationErrors.length) {
                this.warningsConfirmed = false;
                this.notify("danger", this.validationErrors[0]);
                return;
            }
            if (status === "submitted" && this.warnings.length && !this.warningsConfirmed) {
                this.showWarnings = true;
                return;
            }
            if (this.saving) return;

            this.showWarnings = false;
            this.saving = true;
            window.axios
                .post(this.storeUrl, {
                    ...this.payload,
                    status,
                    manager_staff_id: this.managerStaffId,
                    publish_min: this.PUBLISH_MIN,
                    project_min: this.PROJECT_MIN,
                    active_avg: this.ACTIVE_AVG,
                    credit_target: this.CREDIT_TARGET,
                })
                .then(({ data }) => {
                    this.notify("success", data.message || "Report saved.");
                    if (data.redirect) {
                        this.leaving = true;
                        window.location = data.redirect;
                    } else {
                        this.saving = false;
                    }
                })
                .catch((error) => {
                    this.saving = false;
                    const res = error.response;
                    if (res && res.status === 422 && res.data && res.data.errors) {
                        const first = Object.values(res.data.errors)[0];
                        this.notify("danger", Array.isArray(first) ? first[0] : String(first));
                        return;
                    }
                    if (res && res.status === 403 && res.data && res.data.message) {
                        this.notify("danger", res.data.message);
                        return;
                    }
                    this.notify("danger", "Could not save the report. Please try again.");
                });
        },
    },
};
</script>

<style scoped>
.daily-report-actions {
    position: sticky;
    bottom: 0;
    z-index: 10;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

.daily-report-warnings-badge {
    cursor: pointer;
}

.daily-report-preview {
    white-space: pre-wrap;
    font-size: 0.875rem;
    line-height: 1.5;
}

.daily-report .table td,
.daily-report .table th {
    vertical-align: middle;
}

.daily-report .block-options .btn {
    white-space: nowrap;
}
</style>
